import { Send, ThumbDown, ThumbUp } from "@mui/icons-material";
import { Box, Button, Card, CircularProgress, debounce, IconButton, InputAdornment, LinearProgress, TextField, Typography } from "@mui/material";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useIssueRecommendation } from "../../../hooks/process/useIssueRecommendation";
import { axiosInstance } from "../../../services/axios";
import ChatBubble from "../chatBubble";
import CommonPopper from "../commonPopper";
import ReactMarkdownStyled from "../reactMarkdownStyled";


const SUGGESTED_QUESTIONS = [
    "What is the impact of this issue?",
    "How can I fix this?",
    "Can you explain this issue?"
];

export const AIAvatar = () => (
    <Box
        sx={{
            width: 30,
            height: 30,
            borderRadius: "50%",
            background: "linear-gradient(45deg, #00aefd, #2575fc)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            boxShadow: "0px 0px 10px #00aefd",
            color: "white",
            fontSize: 12,
            fontWeight: "bold",
        }}
    >
        AI
    </Box>
);

const FineTuneTag: FC<any> = ({ issueId, issueInfo, resultId = undefined }) => {

    const [response, setResponse] = useState<string | null>(null);
    const [chatHistory, setChatHistory] = useState<{ source: string; message: string }[]>([]);
    const [chatInput, setChatInput] = useState("");
    const [fetchingResponse, setFetchingResponse] = useState(false)
    const chatBoxRef = useRef<HTMLDivElement | null>(null);
    const [submittingFeedback, setSubmittingFeedback] = useState<boolean>(false);
    const { loading, recommendation, comment, selected, setComment, submitFeedback, setSelected } = useIssueRecommendation(issueId);


    const handleThumbClick = (choice: "up" | "down") => {
        setSelected(choice);
        submitFeedback(choice === "up");
    };

    const debouncedSubmitComment = useCallback(
        debounce((text: string) => {
            submitFeedback(selected === "up", text);
        }, 500),
        [selected]
    );

    const handleCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const text = event.target.value;
        setComment(text);
        debouncedSubmitComment(text);
    };

    const trimIssueInfo = (issueInfo: any) => {
        if (!issueInfo || typeof issueInfo !== 'object') return null;

        const {
            id, state, status, createdAt, updatedAt, risk, faultId,
            findingName, findingsCount, projectName, assetName, environmentName,
            latestFindingInfo
        } = issueInfo;

        return {
            id,
            state,
            status,
            createdAt,
            updatedAt,
            risk,
            faultId,
            findingName,
            findingsCount,
            projectName,
            assetName,
            environmentName,
            latestFindingInfo: latestFindingInfo ? {
                path: latestFindingInfo.details?.path,
                line: latestFindingInfo.details?.start?.line,
                message: latestFindingInfo.details?.extra?.message,
                remedy: latestFindingInfo.details?.extra?.references,
                evidenceUrl: latestFindingInfo.evidenceUrl,
                severity: latestFindingInfo.severity
            } : undefined
        };
    }


    const handleChatSubmit = async (question: string | undefined = undefined) => {
        let input = question || chatInput
        if (!input.trim()) return;
        setFetchingResponse(true)

        const newMessage = { source: "human", message: input };
        setChatHistory((prev) => [...prev, newMessage]);
        setChatInput("");
        let filteredIssueInfo = { ...issueInfo }
        delete filteredIssueInfo.severity
        try {
            const response = await axiosInstance.post(`/chat/issue/${issueId}`, {
                issueInfo: trimIssueInfo(filteredIssueInfo),
                chatHistory: [...chatHistory, newMessage],
                resultId: resultId
            });
            setChatHistory((prev) => [...prev, { source: "ai", message: response.data || "Failed to respond" }]);
        } catch (error) {
            console.error("Error sending chat:", error);
            setChatHistory((prev) => [...prev, { source: "ai", message: "Error processing request." }]);
        }
        setFetchingResponse(false)
    };


    useEffect(() => {
        if (chatBoxRef.current) {
            chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
        }
    }, [chatHistory]);


    return (
        <CommonPopper elevatePopper disableHideOnClick
            trigger={<Box
                className="hand"
                sx={{
                    position: "fixed",
                    right: "3em",
                    bottom: "3em",
                    zIndex: 99,
                    px: 3,
                    py: 2,
                    borderRadius: "12px",
                    backgroundColor: "white",
                    color: "black",
                    boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    '&:hover': {
                        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
                    },
                    '&:active': {
                        boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.1)",
                    }
                }}
            >
                <Box display="flex" alignItems="center" gap={2}>
                    <AIAvatar />
                    <Typography variant="h6">
                        Aptori Intelligence
                    </Typography>
                </Box>
            </Box>}
            child={<Card elevation={12} sx={{ p: 2, color: "white", borderRadius: 2, }}>





                <Box ref={chatBoxRef} sx={{ maxHeight: "40vh", overflowY: "auto", minHeight: "20em", width: "30em" }}>
                    <ChatBubble position="left">

                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Hi! I'm here to help you with your issue. You can ask me questions or provide feedback.
                        </Typography>

                    </ChatBubble>
                    {chatHistory.length === 0 && !fetchingResponse && (
                        <Box sx={{ mt: 2, textAlign: "right" }}>
                            {SUGGESTED_QUESTIONS.map((question, index) => (
                                <Box sx={{ pb: 0.5 }}>
                                    <Button
                                        key={index}
                                        variant="outlined"
                                        size="small"
                                        sx={{ color: 'text.primary', borderColor: 'neutral.300' }}
                                        onClick={() => {
                                            handleChatSubmit(question);
                                        }}
                                    >
                                        {question}
                                    </Button>
                                </Box>
                            ))}
                        </Box>
                    )}
                    {chatHistory.map((chat, index) => (
                        <ChatBubble position={chat.source === "human" ? "right" : "left"}>
                            <ReactMarkdownStyled
                                markdown={chat.message || "Please ask something else."}
                           />
                        </ChatBubble>
                    ))}

                          

                </Box>
                {
                    fetchingResponse && <Box>
                        <Typography variant="caption">Responding</Typography>
                        <LinearProgress />
                    </Box>
                }

                <Box sx={{ display: "flex", justifyContent: "start" }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Any questions?"
                        value={chatInput}
                        size="small"
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                event.preventDefault();
                                handleChatSubmit();
                            }
                        }}
                        sx={{ mt: 2 }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => handleChatSubmit()} size="small">
                                        <Send sx={{ mr: 1 }} />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
                <Typography variant="body2" sx={{ mt: 2, }}>
                    Is this issue important? Your feedback helps me improve recommendations.
                </Typography>
                <Box sx={{ display: "flex", mt: 2 }}>
                    <IconButton onClick={() => handleThumbClick("up")} color={selected === "up" ? "success" : "default"}>
                        <ThumbUp fontSize="small" />
                    </IconButton>
                    <IconButton onClick={() => handleThumbClick("down")} color={selected === "down" ? "success" : "default"}>
                        <ThumbDown fontSize="small" />
                    </IconButton>
                </Box>

                {selected !== null && (
                    <CommonPopper elevatePopper disableHideOnClick trigger={<Box display="flex" alignItems="center" sx={{ cursor: "pointer" }} >
                        <Button size="small" variant="outlined" sx={{ color: "text.primary", borderColor: "neutral.300" }}>Add a Comment</Button>
                    </Box>}
                        child={<Box sx={{ p: 2, width: "20em" }}>
                            <TextField
                                multiline
                                minRows={2}
                                fullWidth
                                variant="outlined"
                                value={comment}
                                placeholder="Add a Comment"
                                onChange={handleCommentChange}
                            />
                            <Box sx={{ minHeight: "1.8em" }}>
                                {submittingFeedback ? <CircularProgress size={24} sx={{ mt: 2 }} /> : <Typography variant="body2" sx={{ mt: 2 }}>{response}</Typography>}
                            </Box>
                        </Box>}>





                    </CommonPopper>
                )}

            </Card>} />
    );
};

export default FineTuneTag;
